import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { EnhancedPromptProfileDialog } from '../components/EnhancedPromptProfileDialog';
import {
  getPromptProfiles,
  createPromptProfile,
  updatePromptProfile,
  deletePromptProfile,
  previewPromptByProfileId,
  PromptProfile
} from '../api/promptManagement';

// Styled Components
const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e1e5e9;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  margin: 8px 0 0 0;
  font-size: 1.1rem;
`;

const ActionButton = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => {
    if (props.$danger) return '#e74c3c';
    if (props.$primary) return '#667eea';
    return '#e1e5e9';
  }};
  border-radius: 8px;
  background: ${props => {
    if (props.$danger) return '#e74c3c';
    if (props.$primary) return '#667eea';
    return 'white';
  }};
  color: ${props => props.$primary || props.$danger ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 12px;

  &:hover {
    background: ${props => {
      if (props.$danger) return '#c0392b';
      if (props.$primary) return '#5a6fd8';
      return '#f8f9fa';
    }};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #667eea;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  color: #7f8c8d;
  font-size: 14px;
  font-weight: 500;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  min-width: 250px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ProfilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 2px solid #e1e5e9;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    border-color: #667eea;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const ProfileTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
`;

const ProfileType = styled.span<{ $type: 'image' | 'video' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$type === 'image' ? '#e8f5e8' : '#e8f4fd'};
  color: ${props => props.$type === 'image' ? '#27ae60' : '#3498db'};
`;

const ProfileDescription = styled.p`
  color: #7f8c8d;
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const ProfileMeta = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #6c757d;
`;

const ProfileMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ProfileActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e1e5e9;
`;

const CardButton = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${props => {
    if (props.$danger) return '#e74c3c';
    if (props.$primary) return '#667eea';
    return '#e1e5e9';
  }};
  border-radius: 4px;
  background: ${props => {
    if (props.$danger) return '#e74c3c';
    if (props.$primary) return '#667eea';
    return 'white';
  }};
  color: ${props => props.$primary || props.$danger ? 'white' : '#2c3e50'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => {
      if (props.$danger) return '#c0392b';
      if (props.$primary) return '#5a6fd8';
      return '#f8f9fa';
    }};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px;
  color: #7f8c8d;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  background: #fdf2f2;
  color: #e74c3c;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #fecaca;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  color: #27ae60;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #bbf7d0;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyStateTitle = styled.h3`
  color: #2c3e50;
  margin: 0 0 8px 0;
  font-size: 1.5rem;
`;

const EmptyStateText = styled.p`
  margin: 0 0 24px 0;
  font-size: 1rem;
`;

const BulkActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
`;

const BulkActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  height: 40px;
  border: 2px solid ${props => {
    if (props.$variant === 'primary') return '#667eea';
    if (props.$variant === 'danger') return '#e74c3c';
    return '#e1e5e9';
  }};
  border-radius: 6px;
  background: ${props => {
    if (props.$variant === 'primary') return '#667eea';
    if (props.$variant === 'danger') return '#e74c3c';
    return 'white';
  }};
  color: ${props => props.$variant ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => {
      if (props.$variant === 'primary') return '#5a6fd8';
      if (props.$variant === 'danger') return '#c0392b';
      return '#f8f9fa';
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  height: 40px;
  
  input[type="file"] {
    display: none;
  }
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6c757d;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  transform: scale(1.2);
`;

const EnhancedPromptManagementPage: React.FC = () => {
  const [profiles, setProfiles] = useState<PromptProfile[]>([]);
  const [filteredProfiles, setFilteredProfiles] = useState<PromptProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<PromptProfile | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>('all');
  const [filterAssetType, setFilterAssetType] = useState('all');
  const [stats, setStats] = useState({ total: 0, image: 0, video: 0, default: 0 });
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => { loadProfiles(); }, []);

  useEffect(() => {
    let filtered = profiles;
    if (searchTerm) {
      filtered = filtered.filter(profile =>
        profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        profile.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        profile.asset_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterType !== 'all') {
      filtered = filtered.filter(profile => profile.prompt_type === filterType);
    }
    if (filterAssetType !== 'all') {
      filtered = filtered.filter(profile => profile.asset_type === filterAssetType);
    }
    setFilteredProfiles(filtered);
  }, [profiles, searchTerm, filterType, filterAssetType]);

  useEffect(() => {
    const total = profiles.length;
    const image = profiles.filter(p => p.prompt_type === 'image').length;
    const video = profiles.filter(p => p.prompt_type === 'video').length;
    const defaultProfiles = profiles.filter(p => p.is_default).length;
    setStats({ total, image, video, default: defaultProfiles });
  }, [profiles]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPromptProfiles();
      setProfiles(response.profiles || []);
    } catch (err) {
      setError('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = () => {
    setSelectedProfile(null);
    setDialogMode('create');
    setIsDialogOpen(true);
  };

  const handleEditProfile = (profile: PromptProfile) => {
    setSelectedProfile(profile);
    setDialogMode('edit');
    setIsDialogOpen(true);
  };

  const handleDeleteProfile = async (profile: PromptProfile) => {
    try {
      setError(null);
      await deletePromptProfile(profile.profile_id);
      setSuccessMessage('Profile deleted successfully!');
      await loadProfiles();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to delete profile');
    }
  };

  const handleSaveProfile = async (profileData: PromptProfile) => {
    try {
      setError(null);
      if (dialogMode === 'create') {
        const result = await createPromptProfile(profileData);
        setSuccessMessage('Profile created successfully!');
      } else {
        if (!selectedProfile?.profile_id) throw new Error('No profile_id for update');
        const result = await updatePromptProfile({ ...profileData, profile_id: selectedProfile.profile_id });
        setSuccessMessage('Profile updated successfully!');
      }
      await loadProfiles();
      setIsDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(dialogMode === 'create' ? 'Failed to create profile' : 'Failed to update profile');
      throw err;
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProfile(null);
  };

  const getAssetTypeOptions = () => {
    const assetTypesSet = new Set(profiles.map(p => p.asset_type).filter(Boolean));
    const assetTypes = Array.from(assetTypesSet);
    return ['all', ...assetTypes];
  };

  const handlePreviewProfile = async (profile: PromptProfile) => {
    try {
      console.log('🔍 Previewing profile:', profile.profile_id);
      
      // Show asset type selection dialog
      const assetTypes = ['image', 'video', 'animated', '3d_model', 'audio'];
      const selectedAssetType = await new Promise<string>((resolve) => {
        const dialog = document.createElement('div');
        dialog.style.cssText = `
          position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; 
          background: rgba(0,0,0,0.5); z-index: 10000; display: flex; 
          align-items: center; justify-content: center;
        `;
        
        dialog.innerHTML = `
          <div style="background: white; padding: 30px; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.3); max-width: 500px; width: 90%;">
            <h2 style="margin: 0 0 20px 0; color: #2c3e50;">Select Asset Type for Preview</h2>
            <p style="margin: 0 0 20px 0; color: #666;">Choose which asset type to preview for: <strong>${profile.name}</strong></p>
            <div style="margin-bottom: 20px;">
              ${assetTypes.map(type => `
                <label style="display: block; margin-bottom: 10px; cursor: pointer;">
                  <input type="radio" name="assetType" value="${type}" style="margin-right: 8px;">
                  ${type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')} ${type === '3d_model' ? '🎮' : type === 'image' ? '🖼️' : type === 'video' ? '📹' : type === 'animated' ? '🎬' : '🔊'}
                </label>
              `).join('')}
            </div>
            <div style="display: flex; gap: 10px; justify-content: flex-end;">
              <button id="cancelBtn" style="padding: 10px 20px; border: 1px solid #ddd; background: white; border-radius: 6px; cursor: pointer;">Cancel</button>
              <button id="previewBtn" style="padding: 10px 20px; border: none; background: #3498db; color: white; border-radius: 6px; cursor: pointer;">Preview</button>
            </div>
          </div>
        `;
        
        document.body.appendChild(dialog);
        
        const cancelBtn = dialog.querySelector('#cancelBtn');
        const previewBtn = dialog.querySelector('#previewBtn');
        
        cancelBtn?.addEventListener('click', () => {
          document.body.removeChild(dialog);
          resolve('');
        });
        
        previewBtn?.addEventListener('click', () => {
          const selected = dialog.querySelector('input[name="assetType"]:checked') as HTMLInputElement;
          document.body.removeChild(dialog);
          resolve(selected?.value || 'image');
        });
      });
      
      if (!selectedAssetType) return; // User cancelled
      
      // Use the new simplified preview API with selected asset type
      const result = await previewPromptByProfileId(profile.profile_id, selectedAssetType);
      
      console.log('🔍 Preview response:', result);
      
      const previewWindow = window.open('', '_blank');
      if (previewWindow) {
        previewWindow.document.write(`
          <html><head><title>Prompt Preview - ${profile.name} (${selectedAssetType})</title><style>body { font-family: Arial, sans-serif; margin: 20px; } .section { margin-bottom: 20px; } .title { font-weight: bold; margin-bottom: 10px; } .content { background: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; } .metadata-item { margin-bottom: 5px; } .metadata-key { font-weight: bold; color: #333; } .metadata-value { color: #666; }</style></head><body>
          <h1>Prompt Preview: ${profile.name} (${selectedAssetType.charAt(0).toUpperCase() + selectedAssetType.slice(1).replace('_', ' ')})</h1>
          <div class="section"><div class="title">System Prompt:</div><div class="content">${result.system_prompt || 'No system prompt available'}</div></div>
          <div class="section"><div class="title">User Prompt:</div><div class="content">${result.user_prompt || 'No user prompt available'}</div></div>
          <div class="section"><div class="title">Metadata:</div><div class="content">
            ${result.metadata ? Object.entries(result.metadata)
              .filter(([key]) => ['profile_id', 'profile_name', 'profile_version'].includes(key)) // Show only essential profile info
              .map(([key, value]) => 
                `<div class="metadata-item"><span class="metadata-key">${key}:</span> <span class="metadata-value">${typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</span></div>`
              ).join('') : 'No metadata available'}
          </div></div>
          ${result.warnings && result.warnings.length > 0 ? `<div class="section"><div class="title">Warnings:</div><div class="content">${result.warnings.join('\n')}</div></div>` : ''}
          <div class="section"><div class="title">Custom Prompt Used:</div><div class="content">${result.custom_prompt_used ? 'Yes' : 'No'}</div></div>
          </body></html>
        `);
        previewWindow.document.close();
      }
    } catch (error) {
      console.error('Preview error:', error);
      alert('Failed to generate preview. Please try again.');
    }
  };

  // Handle profile selection
  const handleProfileSelect = (profileId: string, checked: boolean) => {
    const newSelected = new Set(selectedProfiles);
    if (checked) {
      newSelected.add(profileId);
    } else {
      newSelected.delete(profileId);
    }
    setSelectedProfiles(newSelected);
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProfiles(new Set(filteredProfiles.map(p => p.profile_id)));
      setSelectAll(true);
    } else {
      setSelectedProfiles(new Set());
      setSelectAll(false);
    }
  };

  // Export selected profiles
  const handleExportSelected = () => {
    if (selectedProfiles.size === 0) {
      setError('Please select profiles to export');
      return;
    }

    const profilesToExport = profiles.filter(p => selectedProfiles.has(p.profile_id));
    const exportData = {
      exported_at: new Date().toISOString(),
      version: '1.0',
      profiles: profilesToExport
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-profiles-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSuccessMessage(`Exported ${profilesToExport.length} profile(s) successfully!`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Export all profiles
  const handleExportAll = () => {
    const exportData = {
      exported_at: new Date().toISOString(),
      version: '1.0',
      profiles: profiles
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-prompt-profiles-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSuccessMessage(`Exported all ${profiles.length} profile(s) successfully!`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Import profiles
  const handleImportProfiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);

        if (!importData.profiles || !Array.isArray(importData.profiles)) {
          setError('Invalid import file format. Expected profiles array.');
          return;
        }

        let successCount = 0;
        let errorCount = 0;

        for (const profileData of importData.profiles) {
          try {
            // Remove profile_id for new creation
            const { profile_id, ...createData } = profileData;
            await createPromptProfile(createData);
            successCount++;
          } catch (err) {
            console.error('Failed to import profile:', profileData.name, err);
            errorCount++;
          }
        }

        await loadProfiles();

        if (errorCount === 0) {
          setSuccessMessage(`Successfully imported ${successCount} profile(s)!`);
        } else {
          setSuccessMessage(`Imported ${successCount} profile(s), ${errorCount} failed.`);
        }
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error) {
        setError('Failed to parse import file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  // Delete selected profiles
  const handleDeleteSelected = async () => {
    if (selectedProfiles.size === 0) {
      setError('Please select profiles to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedProfiles.size} profile(s)?`)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const profileId of Array.from(selectedProfiles)) {
      try {
        await deletePromptProfile(profileId);
        successCount++;
      } catch (err) {
        console.error('Failed to delete profile:', profileId, err);
        errorCount++;
      }
    }

    await loadProfiles();
    setSelectedProfiles(new Set());
    setSelectAll(false);

    if (errorCount === 0) {
      setSuccessMessage(`Successfully deleted ${successCount} profile(s)!`);
    } else {
      setSuccessMessage(`Deleted ${successCount} profile(s), ${errorCount} failed.`);
    }
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  if (loading) {
    return (<PageContainer><LoadingMessage>Loading prompt profiles...</LoadingMessage></PageContainer>);
  }

  return (
    <PageContainer>
      <Header>
        <div>
          <Title>Enhanced Prompt Management</Title>
          <Subtitle>Comprehensive prompt profile management with system/user focus areas, role-based instructions, and access control</Subtitle>
        </div>
        <ActionButton $primary onClick={handleCreateProfile}>+ Create New Profile</ActionButton>
      </Header>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      
      <StatsContainer>
        <StatCard><StatNumber>{stats.total}</StatNumber><StatLabel>Total Profiles</StatLabel></StatCard>
        <StatCard><StatNumber>{stats.image}</StatNumber><StatLabel>Image Profiles</StatLabel></StatCard>
        <StatCard><StatNumber>{stats.video}</StatNumber><StatLabel>Video Profiles</StatLabel></StatCard>
        <StatCard><StatNumber>{stats.default}</StatNumber><StatLabel>Default Profiles</StatLabel></StatCard>
      </StatsContainer>

      <FiltersContainer>
        <SearchInput 
          type="text" 
          placeholder="Search profiles..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
        />
        <FilterSelect 
          value={filterType} 
          onChange={(e) => setFilterType(e.target.value as 'all' | 'image' | 'video')}
        >
          <option value="all">All Types</option>
          <option value="image">Image Only</option>
          <option value="video">Video Only</option>
        </FilterSelect>
        <FilterSelect 
          value={filterAssetType} 
          onChange={(e) => setFilterAssetType(e.target.value)}
        >
          <option value="all">All Asset Types</option>
          {getAssetTypeOptions().slice(1).map(assetType => (
            <option key={assetType} value={assetType}>
              {assetType.charAt(0).toUpperCase() + assetType.slice(1)}
            </option>
          ))}
        </FilterSelect>
      </FiltersContainer>

      {filteredProfiles.length > 0 && (
        <BulkActionsContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Checkbox
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <SelectionInfo>
              {selectedProfiles.size > 0 
                ? `${selectedProfiles.size} profile(s) selected`
                : 'Select profiles for bulk operations'
              }
            </SelectionInfo>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <BulkActionButton
              onClick={handleExportSelected}
              disabled={selectedProfiles.size === 0}
            >
              Export Selected
            </BulkActionButton>
            <BulkActionButton
              onClick={handleExportAll}
            >
              Export All
            </BulkActionButton>
            <FileInputLabel>
              <input
                type="file"
                accept=".json"
                onChange={handleImportProfiles}
              />
              <BulkActionButton as="span">
                Import Profiles
              </BulkActionButton>
            </FileInputLabel>
            <BulkActionButton
              $variant="danger"
              onClick={handleDeleteSelected}
              disabled={selectedProfiles.size === 0}
            >
              Delete Selected
            </BulkActionButton>
          </div>
        </BulkActionsContainer>
      )}

      {filteredProfiles.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>📝</EmptyStateIcon>
          <EmptyStateTitle>No profiles found</EmptyStateTitle>
          <EmptyStateText>
            {searchTerm || filterType !== 'all' || filterAssetType !== 'all' 
              ? 'Try adjusting your search or filters' 
              : 'Create your first prompt profile to get started'
            }
          </EmptyStateText>
          {!searchTerm && filterType === 'all' && filterAssetType === 'all' && (
            <ActionButton $primary onClick={handleCreateProfile}>Create First Profile</ActionButton>
          )}
        </EmptyState>
      ) : (
        <ProfilesGrid>
          {filteredProfiles.map((profile) => (
            <ProfileCard key={profile.profile_id}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Checkbox
                  type="checkbox"
                  checked={selectedProfiles.has(profile.profile_id)}
                  onChange={(e) => handleProfileSelect(profile.profile_id, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div 
                  style={{ flex: 1, cursor: 'pointer' }}
                  onClick={() => handleEditProfile(profile)}
                >
                  <ProfileHeader>
                    <ProfileTitle>{profile.name}</ProfileTitle>
                    <ProfileType $type={profile.prompt_type}>
                      {profile.prompt_type.toUpperCase()}
                    </ProfileType>
                  </ProfileHeader>
                  <ProfileDescription>
                    {profile.description || 'No description provided'}
                  </ProfileDescription>
                  <ProfileMeta>
                    <ProfileMetaItem><span>📁</span>{profile.asset_type}</ProfileMetaItem>
                    <ProfileMetaItem><span>📅</span>{profile.version || '1.0.0'}</ProfileMetaItem>
                    {profile.is_default && (<ProfileMetaItem><span>⭐</span>Default</ProfileMetaItem>)}
                  </ProfileMeta>
                </div>
              </div>
              
              <ProfileActions onClick={(e) => e.stopPropagation()}>
                <CardButton $primary onClick={() => handleEditProfile(profile)}>Edit</CardButton>
                <CardButton onClick={() => handlePreviewProfile(profile)}>Preview</CardButton>
                <CardButton $danger onClick={() => handleDeleteProfile(profile)}>Delete</CardButton>
              </ProfileActions>
            </ProfileCard>
          ))}
        </ProfilesGrid>
      )}
      
      <EnhancedPromptProfileDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveProfile}
        profile={selectedProfile || undefined}
        mode={dialogMode}
      />
    </PageContainer>
  );
};

export default EnhancedPromptManagementPage; 